import { useCallback, useState } from 'react'
import { useTranslate } from 'react-polyglot'
import { useAsync } from 'react-use'
import { useLocalStorage } from '@rehooks/local-storage'
import { useReportTranslations } from '@ucc/unified-admin-common'

import { useAccountContext } from 'components/contexts/AccountContext'
import { useConnectedUserContext } from 'components/contexts/ConnectedUserContext'
import config from 'config'
import { useLiveJobs } from 'hooks'
import { Activity } from 'types/Activity'
import { Job } from 'types/Job'
import { fetchJobs } from 'utils'
import { getJobActivity } from 'utils/jobs/getJobActivity'

export const getFromDateValueForJobs = (days: number) => new Date(Date.now() - days * 24 * 60 * 60 * 1000).valueOf()

const useActivities = (): Activity[] => {
  const t = useTranslate()
  const reportTranslations = useReportTranslations()
  const iAm = useConnectedUserContext()
  const [lastClear] = useLocalStorage<number>(config.LAST_CLEAR_KEY)
  const { accountKey: selectedAccountKey } = useAccountContext()
  const [activities, setActivities] = useState<Activity[]>([])

  const handleJobCompletion = useCallback(
    (job: Job) => {
      const mappedActivity = getJobActivity(job, t, reportTranslations)

      if (mappedActivity) {
        setActivities((currentActivities) => [...currentActivities, mappedActivity])
      }
    },
    [t, reportTranslations],
  )
  useLiveJobs(handleJobCompletion)

  // Initial jobs from beginning of session up to now
  useAsync(async () => {
    if (selectedAccountKey && iAm?.key /* && lastLogin */) {
      const maxJobsQuerydate = getFromDateValueForJobs(31) // 31 days max query range (EXTERNALADMIN-3632)
      let fromDate = getFromDateValueForJobs(7) // use the last 7 days of activity as the default
      if (lastClear) {
        if (lastClear < maxJobsQuerydate) {
          fromDate = maxJobsQuerydate
        } else {
          fromDate = lastClear
        }
      }

      const jobs = (await fetchJobs({
        accountKey: selectedAccountKey,
        adminKey: iAm.key,
        fromDate,
      })) as Job[]

      if (jobs) {
        const mappedActivities = jobs
          .map((job) => getJobActivity(job, t, reportTranslations))
          .filter((activity) => !!activity) as Activity[]

        setActivities(mappedActivities)
      }
    }
  }, [selectedAccountKey, iAm?.key, lastClear])

  return activities
}

export default useActivities
